import { useEffect } from "react";

/**
 * Preloader — Approach 1: CSS clip-path reveal
 *
 * Performance profile:
 * - Single DOM node for the text (best LCP)
 * - clip-path & transform are GPU-composited (no layout/paint)
 * - No JS animation frames, no per-letter opacity tricks
 * - Text is immediately in DOM → LCP fires on first paint
 */

const Preloader = ({ onDone }) => {
  // Call onDone after animations finish (optional — wire up in parent)
  useEffect(() => {
    const t = setTimeout(onDone, 2400);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="preloader-root">
      <div className="preloader-inner">
        {/* Decorative top rule */}
        <div className="preloader-rule rule-top" />

        {/* Main text — entire string in DOM on frame 1 → LCP candidate */}
        <h1 className="preloader-title">sanguine accent</h1>

        {/* Thin animated underline */}
        <div className="preloader-line" />

        {/* Tagline */}
        <p className="preloader-sub">Make it. Mean it.</p>

        {/* Decorative bottom rule */}
        <div className="preloader-rule rule-bottom" />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400&display=swap');

        /* ── Reset / root ─────────────────────────────── */
        .preloader-root {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f3ea;
          z-index: 9999;
          padding: 24px;
          box-sizing: border-box;
        }

        .preloader-inner {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          max-width: 700px;
        }

        /* ── Title ────────────────────────────────────── */
        .preloader-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 400;
          color: #BC3F4A;
          margin: 0;
          text-align: center;
          line-height: 1.1;

          /* Fluid type: 2rem on tiny phones → 5rem on desktop */
          font-size: clamp(2rem, 10vw, 5rem);

          /* clip-path reveal — GPU composited, no opacity tricks */
          clip-path: inset(0 100% 0 0);
          animation: clip-reveal 1.1s cubic-bezier(0.77, 0, 0.18, 1) 0.2s forwards;
        }

        /* ── Animated underline ───────────────────────── */
        .preloader-line {
          height: 1.5px;
          background: #BC3F4A;
          width: clamp(40px, 12vw, 72px);
          margin-top: clamp(12px, 3vw, 20px);
          transform-origin: left;
          transform: scaleX(0);
          animation: line-grow 0.7s cubic-bezier(0.77, 0, 0.18, 1) 1.05s forwards;
        }

        /* ── Tagline ──────────────────────────────────── */
        .preloader-sub {
          font-family: 'Courier New', monospace;
          font-size: clamp(0.55rem, 2vw, 0.7rem);
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #b0a99f;
          margin: clamp(10px, 2.5vw, 16px) 0 0;
          clip-path: inset(0 100% 0 0);
          animation: clip-reveal 0.6s ease 1.6s forwards;
          white-space: nowrap;
        }

        /* ── Decorative rules ─────────────────────────── */
        .preloader-rule {
          position: absolute;
          left: -5vw;
          right: -5vw;
          height: 1px;
          background: linear-gradient(
            to right,
            transparent,
            rgba(188, 63, 74, 0.25),
            transparent
          );
          opacity: 0;
          animation: fade-in 1s ease 0.8s forwards;
        }
        .rule-top    { top: -32px; }
        .rule-bottom { bottom: -32px; }

        /* ── Keyframes ────────────────────────────────── */
        @keyframes clip-reveal {
          from { clip-path: inset(0 100% 0 0); }
          to   { clip-path: inset(0 0%   0 0); }
        }

        @keyframes line-grow {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }

        @keyframes fade-in {
          to { opacity: 1; }
        }

        /* ── Respect reduced-motion preference ─────────── */
        @media (prefers-reduced-motion: reduce) {
          .preloader-title,
          .preloader-sub {
            clip-path: none;
            animation: none;
          }
          .preloader-line {
            transform: scaleX(1);
            animation: none;
          }
        }

        /* ── Portrait phone (≤ 480px) tweaks ───────────── */
        @media (max-width: 480px) {
          .preloader-rule {
            left: -24px;
            right: -24px;
          }
          .rule-top    { top: -20px; }
          .rule-bottom { bottom: -20px; }
        }
      `}</style>
    </div>
  );
};

export default Preloader;